import React from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children?: React.ReactNode; // Made optional to support icon-only buttons
}

const buttonVariants = {
  primary: [
    'bg-primary text-white',
    'hover:bg-primary/90 active:bg-primary/80',
    'focus:ring-primary',
    'shadow-apple-sm hover:shadow-apple-md',
  ],
  secondary: [
    'bg-gray-100 text-gray-900 border border-gray-300',
    'hover:bg-gray-200 active:bg-gray-300',
    'focus:ring-gray-400',
    'shadow-apple-sm hover:shadow-apple-md',
  ],
  ghost: [
    'bg-transparent text-gray-700',
    'hover:bg-gray-100 active:bg-gray-200',
    'focus:ring-gray-400',
  ],
  danger: [
    'bg-apple-red text-white',
    'hover:bg-red-600 active:bg-red-700',
    'focus:ring-apple-red',
    'shadow-apple-sm hover:shadow-apple-md',
  ],
  success: [
    'bg-apple-green text-white',
    'hover:bg-green-600 active:bg-green-700',
    'focus:ring-apple-green',
    'shadow-apple-sm hover:shadow-apple-md',
  ],
};

const buttonSizes = {
  sm: [
    'h-8 px-3 text-sm',
    'rounded-md',
  ],
  md: [
    'h-10 px-4 text-base',
    'rounded-lg',
  ],
  lg: [
    'h-12 px-6 text-lg',
    'rounded-xl',
  ],
};

const baseButtonStyles = [
  'inline-flex items-center justify-center',
  'font-medium font-apple',
  'transition-all duration-200 ease-apple',
  'focus:outline-none focus:ring-2 focus:ring-offset-2',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
  'touch-manipulation',
  'select-none',
];

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          baseButtonStyles,
          buttonVariants[variant],
          buttonSizes[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {/* Left Icon or Loading Spinner */}
        {loading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          leftIcon && <span className="mr-2">{leftIcon}</span>
        )}

        {/* Button Content */}
        {children && <span className={cn(loading && 'opacity-70')}>{children}</span>}

        {/* Right Icon */}
        {rightIcon && !loading && <span className="ml-2">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export interface ButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  className,
  orientation = 'horizontal',
  size,
  variant,
}) => {
  const groupStyles = orientation === 'horizontal' 
    ? 'flex flex-row' 
    : 'flex flex-col';

  return (
    <div className={cn(groupStyles, 'rounded-lg overflow-hidden', className)}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;

        // Clone button with group styles
        return React.cloneElement(child as React.ReactElement<ButtonProps>, {
          size: size || child.props.size,
          variant: variant || child.props.variant,
          className: cn(
            child.props.className,
            // Remove individual border radius for grouped buttons
            orientation === 'horizontal' 
              ? index === 0 
                ? 'rounded-r-none border-r-0' 
                : index === React.Children.count(children) - 1 
                  ? 'rounded-l-none' 
                  : 'rounded-none border-r-0'
              : index === 0 
                ? 'rounded-b-none border-b-0' 
                : index === React.Children.count(children) - 1 
                  ? 'rounded-t-none' 
                  : 'rounded-none border-b-0'
          ),
        });
      })}
    </div>
  );
};

export interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon'> {
  icon: React.ReactNode;
  'aria-label': string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, size = 'md', className, ...props }, ref) => {
    const iconSizes = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    return (
      <Button
        ref={ref}
        size={size}
        className={cn('!px-0 aspect-square', className)}
        {...props}
      >
        <span className={iconSizes[size]}>{icon}</span>
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

export interface FABProps extends Omit<ButtonProps, 'variant' | 'size'> {
  icon: React.ReactNode;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'md' | 'lg';
}

export const FloatingActionButton: React.FC<FABProps> = ({
  icon,
  position = 'bottom-right',
  size = 'lg',
  className,
  ...props
}) => {
  const positionStyles = {
    'bottom-right': 'fixed bottom-6 right-6',
    'bottom-left': 'fixed bottom-6 left-6',
    'top-right': 'fixed top-6 right-6',
    'top-left': 'fixed top-6 left-6',
  };

  const fabSizes = {
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
  };

  const iconSizes = {
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <button
      className={cn(
        baseButtonStyles,
        buttonVariants.primary,
        positionStyles[position],
        fabSizes[size],
        'rounded-full shadow-apple-lg hover:shadow-apple-xl',
        'z-50',
        className
      )}
      {...props}
    >
      <span className={iconSizes[size]}>{icon}</span>
    </button>
  );
};

export interface LinkButtonProps extends Omit<ButtonProps, 'onClick'> {
  href: string;
  external?: boolean;
  replace?: boolean;
}

export const LinkButton: React.FC<LinkButtonProps> = ({
  href,
  external = false,
  replace = false,
  children,
  ...props
}) => {
  const handleClick = () => {
    if (external) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      if (replace) {
        window.location.replace(href);
      } else {
        window.location.href = href;
      }
    }
  };

  return (
    <Button onClick={handleClick} {...props}>
      {children}
    </Button>
  );
};

export default Button;
